import { GET_CHATS } from '@/hooks/query/useQueryChats';
import { invalidateSavedChats } from '@/hooks/query/useQuerySavedChats';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { type ChatListResponse } from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useMutateDeleteChat() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();

  return useMutation({
    mutationFn: async (chatId: string) => {
      await scribeHttpV2.delete(`/chat/${chatId}`);
      return chatId;
    },
    onMutate: (chatId: string) => {
      queryClient.setQueryData<ChatListResponse>([GET_CHATS], (old) => {
        if (!old) return old;
        return {
          ...old,
          chats: old.chats.filter((c) => c.id !== chatId),
        };
      });
      return { previousChats: queryClient.getQueryData<ChatListResponse>([GET_CHATS]) };
    },
    onError: (error) => {
      logger.error({ error });
      toast({
        title: t('chat.deleteErrorTitle'),
        description: t('chat.deleteErrorDescription'),
        tone: 'destructive',
      });
    },
    onSettled: () => {
      // Ensure server state is in sync
      void queryClient.invalidateQueries({ queryKey: [GET_CHATS] });
      void invalidateSavedChats();
    },
  });
}
